import { Alert, Col, Row, ListGroup } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import PropTypes from 'prop-types';
import { faPencil, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { deleteRequest } from 'helpers/api';
import { Button } from 'components/ui/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import pluralize from 'pluralize';

export function BrokerError({ error }) {
  if (error === '') {
    return null;
  }

  return (
    <Alert className="alert-icon mb-2" variant="danger">
      <FontAwesomeIcon icon={faCircleXmark}/>
      <h6 className="alert-heading">{error}</h6>
      <p className="m-0">Please try again or contact support for help.</p>
    </Alert>
  );
}

BrokerError.propTypes = {
  error: PropTypes.string.isRequired
};

export function BrokerPanel({
  cantModifyMessage,
  deleteRecord,
  firstColumnIdentifier,
  openModal,
  recordName,
  records,
  secondColumnIdentifier,
  onClick
}) {
  if (records.length === 0) {
    return (
      <Empty className="bg-white">
        <Empty.Media>
          <FontAwesomeIcon icon={faExclamationCircle} />
        </Empty.Media>
        <Empty.Title>{`No ${pluralize(recordName, 2)} to display`}</Empty.Title>
        <Empty.Content>{`Get started by clicking on the "Add ${recordName}" Button`}</Empty.Content>
        <Empty.Actions>
          <Button label={`Add ${recordName}`} variant="ghost" onClick={onClick} />
        </Empty.Actions>
      </Empty>
    );
  }

  return (
    <ListGroup className="mb-3">
      {records.map(record => (
        <ListGroup.Item as="li" key={record.id}>
          <Row className="flex-grow-1 align-items-center">
            <Col><strong className="fw-semibold">{record[firstColumnIdentifier]}</strong></Col>
            <Col>{record[secondColumnIdentifier]}</Col>
            <Col className="d-flex justify-content-end">
              {record.canModify ? (
                <React.Fragment>
                  <Button
                    icon={faPencil}
                    type="button"
                    variant="ghost"
                    onClick={() => openModal(record.id)}
                  >
                    Edit
                  </Button>
                  <Button
                    className="text-destructive"
                    icon={faTrash}
                    variant="ghost"
                    onClick={() => deleteRecord(record)}
                  />
                </React.Fragment>
              ) : (
                <Button disabled variant="ghost">{cantModifyMessage}</Button>
              )}
            </Col>
          </Row>
        </ListGroup.Item>
      ))}
    </ListGroup>
  );
}

BrokerPanel.propTypes = {
  cantModifyMessage: PropTypes.string.isRequired,
  deleteRecord: PropTypes.func.isRequired,
  firstColumnIdentifier: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  recordName: PropTypes.string.isRequired,
  records: PropTypes.array,
  secondColumnIdentifier: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const alphabeticSort = (records, field) => records.sort((a, b) => a[field] > b[field] ? 1 : -1);

export function modifyRecordArray(newRecord, setRecords, allRecords, fieldToSort, modalObject, closeModal) {
  if (modalObject.id === '') {
    setRecords(alphabeticSort([...allRecords, newRecord], fieldToSort));
  } else {
    const updatedRecords = allRecords.map((record) => {
      if (record.id === modalObject.id) {
        return { ...record, ...newRecord };
      }
      return record;
    });
    setRecords(alphabeticSort(updatedRecords, fieldToSort));
  }

  closeModal();
}

export function getChildCompanyIdFromUrl() {
  // First check for explicit query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const queryParam = urlParams.get('child_company_id');
  if (queryParam) return queryParam;

  // Check if we're on a corporate/companies/:id route
  const pathMatch = window.location.pathname.match(/\/corporate\/companies\/(\d+)/);
  if (pathMatch) return pathMatch[1];

  return null;
}

export function getChildCompanyUrlSuffix() {
  const childCompanyId = getChildCompanyIdFromUrl();
  return childCompanyId ? `?child_company_id=${childCompanyId}` : '';
}

export function deleteRecord(id, title, url, recordType, setRecords, setError, urlSuffix = '') {
  bootbox.confirm({
    centerVertical: true,
    title: `Delete ${recordType} ${title}`,
    message: ` Deleting this ${recordType} cannot be undone`,
    buttons: {
      cancel: {
        className: 'btn-outline-cancel'
      },
      confirm: {
        className: 'btn-primary',
      }
    },
    callback(result) {
      if (!result) return;

      deleteRequest(`${url}/${id}${urlSuffix}`)
        .then(() => setRecords(records => records.filter(record => record.id !== id)))
        .catch(() => {
          setError(`Unable to delete ${recordType} ${title}`);
          setTimeout(() => {
            setError('');
          }, 5000);
        });
    }
  });
}
